import type { FC } from "react";
import React, { useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, useMediaQuery, useTheme } from "@mui/material";
import { getUserData } from "../../nooks/model";
import { createEditorAssetFolder, uploadOptimizedEditorAssetMedia, upsertProcessedFile } from "../../../editor/Nooks";
import { getVideoDuration } from "../../../editor/Utils";
import { PendoService } from "../../../pendoService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateLeft } from "@fortawesome/pro-regular-svg-icons/faArrowRotateLeft";
import { ConfirmationDialog, TruffleDialogTitle } from "@sundaysky/smartvideo-hub-truffle-component-library";
import { getDateForFileName } from "./recordingToolDialogConfig";
import type { MediaStreamType } from "../../../editor/Hooks";
import { Libraries, LibraryOpener } from "../../../editor/types";
import { RECORDINGS_FOLDER_NAME } from "../../../editor/Components/TruffleEditingWorkSpace/Components/RecordingToolLegacy/recordingToolDialogConfigLegacy";
import { assetMediaVar } from "../../../model";
import { localEditorDockedMediaLibraryCurrentFolderId, localEditorSelectedLibraryVar } from "../../../editor/State";
import { GqlClientMediaOrigin } from "../../../../graphql/graphqlGeneratedTypes/graphqlClient";
import { isFeatureEnabled } from "../../nooks/featureFlags";
import { featureFlagConst } from "../../../../../common/featureFlags/featureFlagConst";
import { SskyErrorCode } from "../../../../../common/errors";

type RecordingToolPreviewProps = {
    recordedFile: Blob;
    startRecordingToolSession: (type: MediaStreamType, cameraDevice?: MediaDeviceInfo, microphoneDevice?: MediaDeviceInfo) => Promise<void>
    recordingType: MediaStreamType
    onClose: () => void;
}

export const RecordingToolPreviewDialog: FC<RecordingToolPreviewProps> = (props: RecordingToolPreviewProps) => {
    const theme = useTheme();
    const mediumViewPort = useMediaQuery(theme.breakpoints.up("md"));
    const [leaveConfirmationDialogOpen, setLeaveConfirmationDialogOpen] = React.useState(false);
    const [retakeConfirmationDialogOpen, setRetakeConfirmationDialogOpen] = React.useState(false);
    const [videoBlobUrl, setVideoBlobUrl] = React.useState<string | null>(null);
    const { userName } = getUserData();

    useEffect(() => {
        if (!props.recordedFile) {
            return;
        }

        const blobUrl = URL.createObjectURL(props.recordedFile);
        setVideoBlobUrl(blobUrl);

        return () => {
            URL.revokeObjectURL(blobUrl);
        };
    }, [props.recordedFile]);


    const reRecordClickHandler = async () => {
        props.onClose();
        await props.startRecordingToolSession(props.recordingType);
    };

    const handleLeaveDialog = () => {
        setLeaveConfirmationDialogOpen(false);
        props.onClose();
    };

    const handleSave = async (recordingType: MediaStreamType) => {
        const mediaVar = assetMediaVar();
        const mediaFolders = mediaVar?.mediaFolders;
        const rootFolder = mediaVar?.rootFolder;

        const recordingFolder = mediaFolders.find(({ name, parentFolder }) =>
            name === RECORDINGS_FOLDER_NAME && parentFolder.id === rootFolder.id
        );
        let parentFolderId: string = recordingFolder?.id ?? "";

        if (!recordingFolder) {
            parentFolderId = await createEditorAssetFolder(RECORDINGS_FOLDER_NAME, rootFolder.id);
        }

        const recordingDuration = await getVideoDuration(videoBlobUrl);

        PendoService.getInstance().trackEvent("Recorder recording saved", {
            mediaStreamType: recordingType,
            mediaDuration: `${recordingDuration?.duration}`
        });

        const fileName = `Recording ${getDateForFileName()} ${userName}.mp4`;
        const file = new File([props.recordedFile], fileName, { type: "video/mp4" });

        upsertProcessedFile({ fileName, status: "loading" });

        handleLeaveDialog();

        await uploadOptimizedEditorAssetMedia(
            file,
            null, // null program ID
            () => {
                localEditorSelectedLibraryVar({ library: Libraries.TruffleMediaLibrary, opener: LibraryOpener.Default });
                localEditorDockedMediaLibraryCurrentFolderId(parentFolderId);
            },
            () => {
                upsertProcessedFile({ sskyCode: SskyErrorCode.AssetMediaUploadError, fileName });
            },
            null,
            parentFolderId,
            false, // Do not skip transcode
            true, // Force transcode
            GqlClientMediaOrigin.RECORDER // Media origin
        );
    };

    const confirmationDialogs: JSX.Element[] = [
        <ConfirmationDialog
            key={"leaveConfirmationDialog"}
            open={leaveConfirmationDialogOpen}
            dialogTitle={"Leave without saving?"}
            onClose={() => setLeaveConfirmationDialogOpen(false)}
            primaryActionButtonText="Leave"
            PrimaryActionButtonProps={{
                onClick: () => handleLeaveDialog(),
                "data-tracking-id": "tracking-id-recorder-preview-leave-approve-and-discard"
            }}
            secondaryActionButtonText="Stay"
            SecondaryActionButtonProps={{
                onClick: () => setLeaveConfirmationDialogOpen(false),
                "data-tracking-id": "tracking-id-recorder-preview-leave-cancel"
            }}
        >
            Your existing footage will be deleted and cannot be recovered.
        </ConfirmationDialog>,
        <ConfirmationDialog
            key={"retakeConfirmationDialog"}
            open={retakeConfirmationDialogOpen}
            dialogTitle={"Re-record your footage?"}
            onClose={() => setRetakeConfirmationDialogOpen(false)}
            primaryActionButtonText="Re-record"
            PrimaryActionButtonProps={{
                onClick: reRecordClickHandler,
                "data-tracking-id": "tracking-id-recorder-preview-rerecord-approve"
            }}
            secondaryActionButtonText="Cancel"
            SecondaryActionButtonProps={{
                onClick: () => setRetakeConfirmationDialogOpen(false),
                "data-tracking-id": "tracking-id-recorder-preview-rerecord-cancel"
            }}
        >
            Your existing footage will be deleted and cannot be recovered.
        </ConfirmationDialog>
    ];

    return <>
        <Dialog
            open={true}
            maxWidth={mediumViewPort ? "md" : "sm"}
            fullWidth
            onClose={() => setLeaveConfirmationDialogOpen(true)}
        >
            <TruffleDialogTitle
                CloseIconButtonProps={{
                    onClick: () => setLeaveConfirmationDialogOpen(true),
                    "data-tracking-id": "tracking-id-recorder-preview-leave-button"
                }}
            >
                Preview Recording
            </TruffleDialogTitle>
            <DialogContent sx={{ position: "relative" }}>
                <video
                    style={{ height: mediumViewPort ? "471px" : "360px", width: "100%", borderRadius: "8px", backgroundColor: theme.palette.other.editorBackground }}
                    src={videoBlobUrl}
                    controls
                    controlsList={"nodownload"}
                    disablePictureInPicture
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => setRetakeConfirmationDialogOpen(true)}
                    startIcon={<FontAwesomeIcon icon={faArrowRotateLeft}/>}
                    color="primary"
                    data-tracking-id="tracking-id-recorder-preview-rerecord-button"
                >
                    Re-record
                </Button>
                <Button
                    variant={"contained"}
                    onClick={() => handleSave(props.recordingType)}
                    color="primary"
                    data-tracking-id="tracking-id-recorder-preview-save-button"
                >
                    Save recording
                </Button>
            </DialogActions>
        </Dialog>

        {confirmationDialogs}
    </>;
};
